/**
 * The web app's Sentry beforeSend, as a function utils/sentry.ts hands to
 * Sentry.init. It lived inline in that init call; it is a module so the
 * keep, drop and demote decisions can be tested on their own and compared
 * against a frozen copy when @mediatechgroup/observability takes over the
 * init policy (PM de2e4507). utils/sentry.ts hands it to the package's
 * browserSentryOptions, whose drops run first and whose redaction runs
 * after; the rules the package makes itself are no longer repeated here.
 * The answers this hook gave before the package are pinned in
 * __tests__/sentryBeforeSend.pinned.test.ts.
 */
import * as Sentry from "@sentry/react";

type InitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
type InitBeforeSend = NonNullable<InitOptions["beforeSend"]>;
export type SentryEvent = Parameters<InitBeforeSend>[0];
export type SentryEventHint = Parameters<InitBeforeSend>[1];
/** The synchronous form: Sentry also accepts a promise, but this one never returns one. */
export type BeforeSend = (event: SentryEvent, hint: SentryEventHint) => SentryEvent | null;

/**
 * @param environment the environment resolved at init time
 * @param env read at send time for REACT_APP_SENTRY_DEBUG (a test seam)
 */
export type EnvLike = Record<string, string | undefined>;

export function makeBeforeSend(environment: string, env: EnvLike = process.env): BeforeSend {
  return function beforeSend(event: SentryEvent, _hint: SentryEventHint) {
    // Don't send events in development unless explicitly enabled
    if (environment === "development" && !env.REACT_APP_SENTRY_DEBUG) {
      return null;
    }

    // Drop everything from an automated browser.
    //
    // The E2E suite deliberately injects failures to prove the app handles
    // them — enrollment.spec.ts route-intercepts /programs-slot with a 403
    // "Simulated permission failure", and other specs return malformed
    // bodies. The app then reports them, exactly as it should, and they land
    // in Sentry looking like production defects. Two of the top issues on
    // 2026-08-22 were this: 66 "Simulated permission failure" and 8
    // "Malformed programs response", every one from a CI runner on
    // /dev-tenant/register.
    //
    // Matching the injected strings would only chase the symptoms — the next
    // spec invents new ones, and "Malformed programs response" is a REAL
    // error the app raises for real users too, so filtering the text would
    // hide the thing worth seeing. `navigator.webdriver` is set only when
    // the browser is under automation, which is never a real user, so it
    // separates the two cleanly and stays correct as the suite grows.
    // The package's policy drops `webdriver === true` before this hook; this
    // check stays because it drops on any truthy value, as it always has.
    if (typeof navigator !== "undefined" && navigator.webdriver) {
      return null;
    }

    const errorType = event.exception?.values?.[0]?.type || "";
    const errorMessage = event.exception?.values?.[0]?.value || "";

    // AbortErrors (user navigation, unmount, axios's "Request aborted") and
    // the Outlook Safe Links scanner's "Object Not Found Matching Id ...
    // MethodName" rejection are dropped by @mediatechgroup/observability's
    // browser policy, which runs before this hook (isAbortedRequest and
    // isLinkScanner match exactly the rules that lived here). Pinned in
    // sentryBeforeSend.pinned.test.ts through the composed beforeSend.

    // Filter out transient network errors — these are not actionable
    // (Netlify cold starts, user connectivity issues, CDN blips)
    if (
      errorMessage === "Failed to fetch" ||
      errorMessage === "Network Error" ||
      errorMessage === "Load failed" ||
      errorMessage.includes("Failed to fetch") ||
      errorMessage.includes("Load failed")
    ) {
      return null;
    }

    // Filter Axios timeout errors — transient network/cold-start issues
    if (errorType === "AxiosError" && errorMessage.includes("timeout of")) {
      return null;
    }

    // Filter out 401 AxiosErrors — expired JWT sessions handled by the app.
    // The axios interceptor skips these, but individual API catch blocks re-capture them.
    //
    // 403s are deliberately NOT dropped anymore: this blanket filter is
    // how the 2026-05→07 member self-pay outage (every payment write
    // 403ing for members) produced zero Sentry signal. A 403 means the
    // backend denied something the UI offered — the captureException/
    // captureApiError wrappers convert those to grouped warning events
    // (capture403Denied); any raw-SDK stragglers landing here get
    // demoted to warning and tagged instead of silently vanishing.
    if (errorType === "AxiosError" && /status code 401/.test(errorMessage)) {
      return null;
    }
    if (errorType === "AxiosError" && /status code 403/.test(errorMessage)) {
      event.level = "warning";
      event.tags = { ...event.tags, error_type: "permission_denied", http_status: "403" };
      return event;
    }

    // Filter out JWT validation errors (these are handled gracefully)
    if (errorMessage.includes("InvalidJWTToken") || errorMessage.includes("Invalid value for JWT claim")) {
      return null;
    }

    // Filter Stripe.js load failures (handled by StripeBlockedAlert UI)
    if (errorMessage.includes("Failed to load Stripe.js")) {
      return null;
    }

    // Filter React DOM errors caused by browser extensions (Grammarly, translate, etc.)
    // modifying the DOM tree that React manages — not actionable
    if (
      errorType === "NotFoundError" &&
      errorMessage.includes("removeChild") &&
      errorMessage.includes("not a child of this node")
    ) {
      return null;
    }

    // Filter intermediate Supabase Realtime channel errors (keep final exhausted-retries errors)
    if (errorMessage.includes("Channel subscription failed") && !errorMessage.includes("retries")) {
      return null;
    }

    // Filter Supabase Storage transient network errors (iOS Safari "Load failed")
    if (errorType === "StorageUnknownError" || (errorType === "StorageError" && errorMessage.includes("Load failed"))) {
      return null;
    }

    // Filter unactionable "empty" unhandled promise rejections — a promise
    // rejected with a value that has no enumerable keys (e.g. `{}` or a
    // thrown non-Error). Sentry renders these as "...promise rejection...
    // [object has no keys]" with no stack and no payload, so there is
    // nothing to act on. (Seen as an UnhandledRejection on the login page.)
    if (errorMessage.includes("promise rejection") && errorMessage.includes("[object has no keys]")) {
      return null;
    }

    // Filter out sensitive data from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
        if (breadcrumb.data) {
          // Remove sensitive fields
          const sensitiveFields = ["password", "token", "apiKey", "authorization"];
          sensitiveFields.forEach((field) => {
            if (breadcrumb.data && field in breadcrumb.data) {
              breadcrumb.data[field] = "[Filtered]";
            }
          });
        }
        return breadcrumb;
      });
    }

    return event;
  };
}
